import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FormSection from '@/components/forms/FormSection';
import Input from '@/components/forms/Input';
import Button from '@/components/ui/Button';
import { useState } from 'react';
import { updateClientPassword } from '@/services/clientAuth';
import showToast from '@/components/ui/Toast';
import { Icon } from '@iconify/react';
// requireArenaSlug not needed for client password update

// Types
interface PasswordChangeValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// Initial Values
const passwordInitial: PasswordChangeValues = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

// Validation Schema
const passwordSchema = Yup.object({
  currentPassword: Yup.string()
    .min(6, 'Password minimal 6 karakter')
    .required('Password saat ini wajib diisi'),
  newPassword: Yup.string()
    .min(8, 'Password minimal 8 karakter')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{}|;:,.<>?]).*$/,
      'Password harus berisi huruf besar, huruf kecil, angka, dan karakter spesial'
    )
    .required('Password baru wajib diisi'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Konfirmasi password tidak cocok')
    .required('Konfirmasi password wajib diisi'),
});

interface AuthSecurityTabProps {
  onSubmit: (values: PasswordChangeValues, tabId: string) => Promise<void>;
}

const AuthSecurityTab = ({ onSubmit }: AuthSecurityTabProps) => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // const handleGoogleConnect = async () => {
  //   try {
  //     // Implement Google OAuth connection logic here
  //     console.log('Connecting with Google...');
  //     // You might want to redirect to Google OAuth or handle the connection
  //     window.location.href = '/auth/google';
  //   } catch (error) {
  //     console.error('Failed to connect with Google:', error);
  //   }
  // };

  return (
    <div className="space-y-8">
      {/* Password Change Section */}
      <div className="bg-white">
        <FormSection title="Ubah Password">
          <Formik
            initialValues={passwordInitial}
            validationSchema={passwordSchema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              try {
                // Prefer client password endpoint
                const res = await updateClientPassword({
                  current_password: values.currentPassword,
                  new_password: values.newPassword,
                  confirm_new_password: values.confirmPassword,
                });

                if (res.success) {
                  showToast.success(res.message || 'Password berhasil diubah');
                  resetForm();
                  await onSubmit(values, 'auth-security');
                } else {
                  const msg = res.message || 'Gagal mengubah password';
                  showToast.error(msg);
                  throw new Error(msg);
                }
              } catch (error: any) {
                const message =
                  error?.response?.data?.message || error?.message || 'Gagal mengubah password';
                showToast.error(message);
                throw error;
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, values }) => (
              <Form className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column - Password Form */}
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Password saat ini
                      </label>
                      <div className="relative">
                        <Input
                          name="currentPassword"
                          type={showCurrentPassword ? 'text' : 'password'}
                          placeholder="••••••"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                          {showCurrentPassword ? (
                            <Icon icon={'mdi:eye-off'} className="h-5 w-5" />
                          ) : (
                            <Icon icon={'mdi:eye'} className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Password Baru
                      </label>
                      <div className="relative">
                        <Input
                          name="newPassword"
                          type={showNewPassword ? 'text' : 'password'}
                          placeholder="Masukkan password baru"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                          {showNewPassword ? (
                            <Icon icon={'mdi:eye-off'} className="h-5 w-5" />
                          ) : (
                            <Icon icon={'mdi:eye'} className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Konfirmasi Password Baru
                      </label>
                      <div className="relative">
                        <Input
                          name="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="••••••"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                          {showConfirmPassword ? (
                            <Icon icon={'mdi:eye-off'} className="h-5 w-5" />
                          ) : (
                            <Icon icon={'mdi:eye'} className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="pt-4">
                      <Button
                        type="submit"
                        variant="primary"
                        loading={isSubmitting}
                        disabled={
                          !values.currentPassword || !values.newPassword || !values.confirmPassword
                        }
                        className="w-full bg-primary-500 hover:bg-primary-600 text-white"
                      >
                        Ubah Password
                      </Button>
                    </div>
                  </div>

                  {/* Right Column - Social Login */}
                  {/* <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Social Login</h3>

                      <div className="space-y-4">
                        <button
                          type="button"
                          onClick={handleGoogleConnect}
                          className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        >
                          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                            <path
                              fill="#4285F4"
                              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            />
                            <path
                              fill="#34A853"
                              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            />
                            <path
                              fill="#FBBC05"
                              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            />
                            <path
                              fill="#EA4335"
                              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            />
                          </svg>
                          Connect with Google
                        </button>

                        <div className="text-sm text-gray-500 mt-2">
                          <p>Hubungkan akun Google Anda untuk login yang lebih mudah</p>
                        </div>
                      </div>
                    </div>
                  </div> */}
                </div>
              </Form>
            )}
          </Formik>
        </FormSection>
      </div>
    </div>
  );
};

export default AuthSecurityTab;
